"use client";

import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from "react";
import { debounce } from "@mui/material";
import {
  Box,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Backdrop,
  IconButton,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSchedule } from "./lib/hooks/use-schedule";
import { useDragDrop } from "./lib/hooks/use-drag-drop";
import { CapacityBoard } from "./capacity-board";
import { BreakSegmentModal } from "./break-segment-modal";
import { WorkOrdersSidebar } from "./work-orders-sidebar";
import { SegmentPartsSidebar } from "./segment-parts-sidebar";
import { isHoliday, isLocationOpen } from "./lib/utils/capacity-calculator";
import type { Segment, WorkOrder, ScheduledItem, SegmentSchedulingStatus } from "./lib/types";
import {
  DEFAULT_SEGMENT_SCHEDULING_FILTERS,
  normalizeSegmentSchedulingStatus,
  isDateTodayOrFuture,
} from "./lib/utils/segment-scheduling-utils";
import SmartWoCreationDialog from "./smartWoCreationDialog/smartWoCreationDialog";
import {
  getDateForDayWithOffset,
  getDayOfWeek,
  capitalizeFirstLetter,
} from "./lib/utils/schedular-utils";
import { useLocation } from "react-router-dom";
import { track } from "@amplitude/analytics-browser";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import { useTechnicianCalendarStore } from "../store/zustandTechnicianCalendarStore";
import { ApplicationUser, SettingsLocation } from "models/lookupTypes";
import SchedulingAPI from "./lib/api/schedulingApi";
import { getCache } from "../common/Utils/cacheUtil";
import { useLookupData } from "../common/hooks/useLookupData";
import type {
  SettingsLocationBusinessHours,
  SettingsLocationHoliday,
  EmployeeSchedule,
  EmployeeScheduleException,
} from "./lib/models";
import WorkOrderDialogContainer from "../workOrder/WorkOrderDialogContainerMain";
import { ConcurrencyDialog } from "../common/components/ConcurrencyDialog";
import { getCachedSegmentEdit, clearCachedSegmentEdit } from "./segment-item";
import { WorkOrderQueueAPI } from "../QueueBoard/api/workOrderApi";
import { useSnackbar } from "notistack";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import localStorageUtil from "../../app/localStorage";
import packageJson from "../../../package.json";
import { User } from "components/authentication/models/authTypes";
import { fadeInUp } from "components/common/global-style/GlobalStyling";
// Performance tracking - render counter
let pageRenderCount = 0;

// HMR Detection: This timestamp changes after each hot reload
// Used to force cache resync after code changes in development
const MODULE_LOAD_TIMESTAMP = Date.now();

// Convert ApplicationUser (from global cache) to Technician format
function convertApplicationUserToTechnician(
  appUser: ApplicationUser,
): ApplicationUser {
  return {
    identity: appUser.identity,
    userName: appUser.userName || `Technician ${appUser.identity}`,
    //specialty: specialties[index % specialties.length],
    //avatarColor: avatarColors[index % avatarColors.length],
    defaultLocationIdentity: appUser.defaultLocationIdentity,
    //hourlyRate: 0, // Not available in ApplicationUser from lookupTypes
    isTechnician: true,
  };
}

// Convert Location (from global cache) to SettingsLocation format
function convertLocationToSettingsLocation(
  location: SettingsLocation,
): SettingsLocation {
  return {
    identity: location.identity,
    displayText: location.displayText,
    companyName: location.companyName,
    GeneralLedgerNumber: location.GeneralLedgerNumber,
    emailAddress: location.emailAddress,
    taxGroupId: location.taxGroupId,
    taxExemptId: location.taxExemptId,
  };
}

function mapSegmentFromApi(seg: any) {
  const tasks = (seg.tasks || seg.Tasks || []).map((task: any) => ({
    segmentTaskId: task.segmentTaskId ?? task.SegmentTaskId,
    status: task.status ?? task.Status ?? 0,
    isActive: task.isActive ?? task.IsActive ?? true,
    estimatedHours: task.estimatedHours ?? task.EstimatedHours ?? 0,
    workDate: task.workDate ?? task.WorkDate,
    technicianName: task.technicianName ?? task.TechnicianName ?? "",
    taskSchedulingStatus:
      task.taskSchedulingStatus ?? task.TaskSchedulingStatus ?? "",
    scheduleItemId: task.scheduleItemId ?? task.ScheduleItemId,
  }));

  return {
    ...seg,
    name:
      seg.name ||
      seg.DisplayText ||
      seg.displayText ||
      seg.segmentName ||
      seg.SegmentName ||
      `Segment ${seg.id}`,
    estimatedHours: seg.estimatedHours || seg.hours || 0,
    hours: seg.hours || seg.estimatedHours || 0,
    segmentParts: seg.segmentParts || [],
    schedulingStatus: normalizeSegmentSchedulingStatus(
      seg.schedulingStatus ?? seg.SchedulingStatus,
    ),
    scheduledHours: seg.scheduledHours ?? seg.ScheduledHours ?? 0,
    remainingHours: seg.remainingHours ?? seg.RemainingHours ?? 0,
    taskCount: seg.taskCount ?? seg.TaskCount ?? tasks.length,
    tasks,
  };
}

export default function Page() {
  // Performance tracking
  //pageRenderCount++;
  //console.log(`[PAGE] Scheduler Page render #${pageRenderCount}`);

  // CRITICAL: Ensure lookup data (including scheduling data) is fetched and cached
  // This is required for capacity calculations to work after page refresh
  useLookupData();

  const schedule = useSchedule();
  const dragDrop = useDragDrop();
  const { technicians: techniciansFromCache, locations: locationsFromCache } =
    useWorkOrderStore();

  // Expose schedule state to window for debugging (only in development)
  // CRITICAL: Use useLayoutEffect to update window reference without causing re-renders
  // This runs synchronously after DOM updates but before browser paint
  useLayoutEffect(() => {
    if (typeof window !== "undefined" && import.meta.env.DEV) {
      (window as any).__scheduleState = schedule.state;
      (window as any).__getScheduleState = () => schedule.state;
    }
  }); // No dependencies - updates window reference on every render without causing loops

  const [breakModalOpen, setBreakModalOpen] = useState(false);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(
    null,
  );
  const [selectedSegmentFromDrag, setSelectedSegmentFromDrag] = useState<{
    segment: Segment;
    workOrder: WorkOrder;
  } | null>(null);
  const [capacityWarning, setCapacityWarning] = useState<{
    technicianName: string;
    day: string;
    availableHours: number;
    requestedHours: number;
  } | null>(null);
  const [overrideRequest, setOverrideRequest] = useState<{
    dragData: any;
    techId: number;
    day: string;
    date?: Date; // CRITICAL: Store date for correct workDate calculation
    position?: number;
    reason: "over-capacity" | "tech-off";
    techName: string;
  } | null>(null);
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(true); // Start collapsed
  const [darkMode, setDarkMode] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [resetSearchTrigger, setResetSearchTrigger] = useState(0);

  // Concurrency dialog state
  const [showConcurrencyDialog, setShowConcurrencyDialog] = useState(false);
  const [concurrencyMessage, setConcurrencyMessage] = useState("");

  // Snackbar for notifications
  const { enqueueSnackbar } = useSnackbar();

  // State for pending segment edit restoration with confirmation
  const [pendingSegmentEdit, setPendingSegmentEdit] = useState<{
    segmentId: string;
    hours: number;
  } | null>(null);
  const [showRestoreSegmentDialog, setShowRestoreSegmentDialog] =
    useState(false);

  // Work order dialog state for opening work orders from scheduled items
  const [showWorkOrderDialog, setShowWorkOrderDialog] = useState(false);
  const [dialogWorkOrderId, setDialogWorkOrderId] = useState<number | null>(
    null,
  );

  // Today's items alert banner state
  const [todayAlertDismissed, setTodayAlertDismissed] = useState(false);
  const [todayItemsCount, setTodayItemsCount] = useState(0);
  const [triggerDayView, setTriggerDayView] = useState(0);

  // Read initial values from URL (passed from WorkOrderFormDialog)
  const urlParams = new URLSearchParams(window.location.search);
  const initialSearchQuery = urlParams.get("search") || "";
  const initialLocationId = urlParams.get("locationId")
    ? parseInt(urlParams.get("locationId")!, 10)
    : null;
  const [user] = useState<User | undefined>(localStorageUtil.getByKey("user"));
  // Clear URL parameters after reading them to prevent repopulating on reload
  useEffect(() => {
    if (initialSearchQuery || initialLocationId !== null) {
      // Remove search params from URL without triggering a page reload
      const url = new URL(window.location.href);
      url.searchParams.delete("search");
      url.searchParams.delete("locationId");
      window.history.replaceState({}, "", url.pathname);
    }
  }, []); // Run only once on mount

  // Check for cached segment edits on mount (after page reload from concurrency conflict)
  useEffect(() => {
    const cachedEdit = getCachedSegmentEdit();
    if (cachedEdit) {
      setPendingSegmentEdit(cachedEdit);
      setShowRestoreSegmentDialog(true);
    }
  }, []); // Run only once on mount

  // Handle user confirming to restore segment edit
  const handleRestoreSegmentConfirm = async () => {
    setShowRestoreSegmentDialog(false);

    if (!pendingSegmentEdit) {
      clearCachedSegmentEdit();
      return;
    }

    const { segmentId, hours } = pendingSegmentEdit;
    const username = localStorage.getItem("username") || "";

    // Find the segment in loaded work orders to get fresh rowVersion
    let targetSegment: Segment | null = null;
    for (const wo of schedule.state.workOrders) {
      const found = wo.segmentList.find((s) => s.id === segmentId);
      if (found) {
        targetSegment = found;
        break;
      }
    }

    if (targetSegment && username) {
      try {
        await WorkOrderQueueAPI.updateSegmentEstimatedHours(
          parseInt(segmentId, 10),
          hours,
          username,
          new Date().toISOString(),
          targetSegment.rowVersion,
        );

        enqueueSnackbar(
          "Your changes have been restored. You can proceed now.",
          { variant: "success", autoHideDuration: 5000 },
        );
        logAmplitudeEventWithProps(
          user.userNameAndCompany,
          {
            company: user.company,
            user: user.userName,
          },
          "updateTechCalendarSgmtEstedHrs",
          {
            elapsed_ms: "0.00",
            title: "Update Estimate Hours",
            componentName: "TechCalendarPage.tsx",
            version: packageJson.version,
          },
        );
        // Trigger a refresh to show the updated data
        window.dispatchEvent(new CustomEvent("refreshWorkOrdersTechCalendar"));
      } catch (error) {
        console.error("Failed to restore segment edit:", error);
        enqueueSnackbar(
          "Could not restore your changes. Please try again manually.",
          { variant: "error", autoHideDuration: 5000 },
        );

        logAmplitudeEventWithProps(
          user.userNameAndCompany,
          {
            company: user.company,
            user: user.userName,
          },
          "updateTechCalendarSgmtEstedHrs",
          {
            elapsed_ms: "0.00",
            title: "Update Estimate Hours - Exception",
            componentName: "page.tsx " + error,
            version: packageJson.version,
          },
        );
      }
    } else if (!targetSegment && schedule.state.workOrders.length > 0) {
      enqueueSnackbar(
        "Could not find the segment. The changes were not restored.",
        { variant: "warning", autoHideDuration: 5000 },
      );
    }

    // Always clear cache and pending edit after user's choice
    setPendingSegmentEdit(null);
    clearCachedSegmentEdit();
  };

  // Handle user declining to restore segment edit
  const handleRestoreSegmentDecline = () => {
    setShowRestoreSegmentDialog(false);
    setPendingSegmentEdit(null);
    clearCachedSegmentEdit();
  };

  // Multi-location support: empty array means "All"
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>(
    initialLocationId !== null ? [initialLocationId] : [],
  );
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [loadingScheduledItems, setLoadingScheduledItems] = useState(false);
  const [workOrderPage, setWorkOrderPage] = useState(1);
  const [workOrderTotalCount, setWorkOrderTotalCount] = useState(0);
  const [workOrderTotalPages, setWorkOrderTotalPages] = useState(0);
  const [workOrderSearchString, setWorkOrderSearchString] =
    useState(initialSearchQuery);
  const [segmentSchedulingStatuses, setSegmentSchedulingStatuses] = useState<
    SegmentSchedulingStatus[]
  >(DEFAULT_SEGMENT_SCHEDULING_FILTERS);
  const [smartWoDialogState, setSmartWoDialogState] = useState<{
    open: boolean;
    technicianId?: number;
    date?: string;
  }>({ open: false });

  // Keep latest WO query params in a ref. Drop handlers are often held by memoized
  // TechnicianRow/TechnicianCard and would otherwise refresh with stale filters
  // (e.g. default "unscheduled" after the user selected "Past Due").
  const workOrderQueryRef = useRef<{
    locationIds: number[];
    pageNumber: number;
    searchString: string;
    woStatusIds: string[];
    woDocStatusIds: string[];
    segmentSchedulingStatuses: SegmentSchedulingStatus[];
  }>({
    locationIds: selectedLocationIds,
    pageNumber: workOrderPage,
    searchString: workOrderSearchString,
    woStatusIds: ["0"],
    woDocStatusIds: ["0"],
    segmentSchedulingStatuses,
  });

  // Keep scheduled items independent per tab (day/week/month).
  // schedule.state.scheduledItems will always represent the ACTIVE tab's items.
  const activeViewModeRef = useRef<"day" | "week" | "month">("week");
  const scheduledItemsByViewRef = useRef<{
    day: ScheduledItem[];
    week: ScheduledItem[];
    month: ScheduledItem[];
  }>({
    day: [],
    week: [],
    month: [],
  });
  const scheduledItemsRequestSeqRef = useRef<{
    day: number;
    week: number;
    month: number;
  }>({
    day: 0,
    week: 0,
    month: 0,
  });
  const dayDateRef = useRef<Date>(new Date());
  const monthDateRef = useRef<Date>(new Date());

  // Helper to get date range for a specific week offset (Sunday to Saturday)
  // weekOffset: 0 = current week, 1 = next week, -1 = previous week, etc.
  const getWeekDateRange = (offset: number) => {
    const today = new Date();
    const currentDay = today.getDay();
    const daysFromSunday = currentDay === 0 ? 0 : currentDay; // Sunday is 0
    const currentSunday = new Date(today);
    currentSunday.setDate(today.getDate() - daysFromSunday);
    currentSunday.setHours(0, 0, 0, 0);

    // Apply week offset
    const sunday = new Date(currentSunday);
    sunday.setDate(currentSunday.getDate() + offset * 7);

    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    saturday.setHours(23, 59, 59, 999);

    return { fromDate: sunday, toDate: saturday };
  };

  //#region Amplitude
  const location = useLocation();
  useEffect(() => {
    track("Technician Schedular Viewed", {
      page: location.pathname,
      title: document.title,
    });
  }, [location]);
  //#endregion

  /**
   * SYNC DATA FROM CACHE: Technicians
   *
   * Scenario 1 - First Page Load:
   *   - Technicians are loaded from global cache (technicians_userName) via useWorkOrderStore
   *   - Cache is populated from API calls made in other components (e.g., FilterPanel)
   *   - This syncs cached technicians into schedule.state for capacity calculations
   *
   * Scenario 2 - Page Refresh:
   *   - Store initializes with cached technicians on mount
   *   - This useEffect ensures technicians are synced to schedule.state even if component
   *     mounts before store is fully initialized
   *   - Only updates if schedule.state is empty to avoid unnecessary re-renders
   */
  useEffect(() => {
    if (techniciansFromCache && techniciansFromCache.length > 0) {
      // Always update if schedule.state is empty (e.g., on page refresh)
      // This ensures technicians are loaded even if the store was initialized before the component mounted
      if (schedule.state.technicians.length === 0) {
        const convertedTechnicians = techniciansFromCache.map((tech, index) =>
          convertApplicationUserToTechnician(tech),
        );
        schedule.setTechnicians(convertedTechnicians);
      }
    }
  }, [
    techniciansFromCache,
    schedule.setTechnicians,
    schedule.state.technicians.length,
  ]);

  /**
   * SYNC DATA FROM CACHE: Locations
   *
   * Scenario 1 - First Page Load:
   *   - Locations are loaded from global cache (locations_userName) via useWorkOrderStore
   *   - Cache is populated from API calls made in other components
   *   - This syncs cached locations into schedule.state for location-based capacity calculations
   *
   * Scenario 2 - Page Refresh:
   *   - Store initializes with cached locations on mount
   *   - This useEffect ensures locations are synced to schedule.state
   *   - Only updates if schedule.state is empty to avoid unnecessary re-renders
   */
  useEffect(() => {
    if (locationsFromCache && locationsFromCache.length > 0) {
      // Only update if schedule.state is empty or if the data has changed
      if (schedule.state.locations.length === 0) {
        const convertedLocations = locationsFromCache.map((loc) =>
          convertLocationToSettingsLocation(loc),
        );
        schedule.setLocations(convertedLocations);
      }
    }
  }, [
    locationsFromCache,
    schedule.setLocations,
    schedule.state.locations.length,
  ]);

  /**
   * SYNC DATA FROM CACHE: Scheduling Data (Employee Schedules, Exceptions, Business Hours, Holidays)
   *
   * useLookupData hook caches scheduling data from API. This effect polls the cache
   * and syncs to schedule.state when data becomes available.
   *
   * Cache keys (cleared on login via clearSchedulingLookupCaches; 24h TTL within session):
   *   - sch_locationBusinessHours
   *   - sch_locationHolidays
   *   - sch_employeeSchedules
   *   - sch_employeeScheduleExceptions
   */
  const {
    weekOffset,
    selectedLocationIds: selectedLocationIdsFromStore, // Get selected locations from store (string array)
    setSelectedLocationIds: setSelectedLocationIdsInStore, // Set selected locations in store
    selectedWoStatusIds, // Get selected WO status IDs from store
    selectedWoDocStatusIds,
    scheduleItemView,
  } = useTechnicianCalendarStore();

  // Keep drop/remove handlers from using stale sidebar filters via memoized board rows
  workOrderQueryRef.current = {
    locationIds: selectedLocationIds,
    pageNumber: workOrderPage,
    searchString: workOrderSearchString,
    woStatusIds: selectedWoStatusIds,
    woDocStatusIds: selectedWoDocStatusIds,
    segmentSchedulingStatuses,
  };

  // Set location in store if passed via URL (from WorkOrderFormDialog)
  useEffect(() => {
    if (initialLocationId !== null) {
      setSelectedLocationIdsInStore([initialLocationId.toString()]);
    }
  }, [initialLocationId, setSelectedLocationIdsInStore]);

  // Track last module load timestamp to detect HMR
  const lastModuleLoadRef = useRef<number>(0);

  // Scheduling data sync from cache to schedule.state
  // This runs periodically until data is loaded, then stops
  // CRITICAL: Also handles HMR by detecting module reload and forcing cache resync
  useEffect(() => {
    const username = localStorage.getItem("username") || "";
    if (!username) return;

    const MAX_CACHE_AGE = 1000 * 60 * 60 * 24; // 24 hours

    // HMR Detection: If module timestamp changed, force a resync
    // This fixes the issue where capacity calculations stop working after code changes
    const isHmrReload =
      lastModuleLoadRef.current !== 0 &&
      lastModuleLoadRef.current !== MODULE_LOAD_TIMESTAMP;

    if (isHmrReload) {
      console.log(
        "[page.tsx] HMR detected - forcing cache resync for capacity calculations",
      );
    }
    lastModuleLoadRef.current = MODULE_LOAD_TIMESTAMP;

    const loadSchedulingDataFromCache = (forceSync: boolean = false) => {
      const cachedBusinessHours = getCache<SettingsLocationBusinessHours[]>(
        "sch_locationBusinessHours",
        username,
        MAX_CACHE_AGE,
      );
      const cachedHolidays = getCache<SettingsLocationHoliday[]>(
        "sch_locationHolidays",
        username,
        MAX_CACHE_AGE,
      );
      const cachedEmployeeSchedules = getCache<EmployeeSchedule[]>(
        "sch_employeeSchedules",
        username,
        MAX_CACHE_AGE,
      );
      const cachedEmployeeScheduleExceptions = getCache<
        EmployeeScheduleException[]
      >("sch_employeeScheduleExceptions", username, MAX_CACHE_AGE);

      // Sync to schedule.state if cache has data
      // CRITICAL: If forceSync is true (HMR detected), always sync regardless of current state
      // This fixes the issue where HMR preserves stale state references
      if (
        cachedBusinessHours &&
        cachedBusinessHours.length > 0 &&
        (forceSync || schedule.state.businessHours.length === 0)
      ) {
        schedule.setBusinessHours(cachedBusinessHours);
      }
      if (
        cachedHolidays &&
        cachedHolidays.length > 0 &&
        (forceSync || schedule.state.holidays.length === 0)
      ) {
        schedule.setHolidays(cachedHolidays);
      }
      if (
        cachedEmployeeSchedules &&
        cachedEmployeeSchedules.length > 0 &&
        (forceSync || schedule.state.employeeSchedules.length === 0)
      ) {
        schedule.setEmployeeSchedules(cachedEmployeeSchedules);
      }
      if (
        cachedEmployeeScheduleExceptions &&
        cachedEmployeeScheduleExceptions.length > 0 &&
        (forceSync || schedule.state.employeeScheduleExceptions.length === 0)
      ) {
        schedule.setEmployeeScheduleExceptions(
          cachedEmployeeScheduleExceptions,
        );
      }

      // Return true if all data is loaded
      return (
        schedule.state.businessHours.length > 0 ||
        (cachedBusinessHours && cachedBusinessHours.length > 0)
      );
    };

    // Load immediately - force sync if HMR detected
    const dataLoaded = loadSchedulingDataFromCache(isHmrReload);

    // If data not loaded, poll every 500ms until it's available (max 10 seconds)
    if (!dataLoaded) {
      let attempts = 0;
      const maxAttempts = 20; // 10 seconds max
      const pollInterval = setInterval(() => {
        attempts++;
        const loaded = loadSchedulingDataFromCache(false);
        if (loaded || attempts >= maxAttempts) {
          clearInterval(pollInterval);
          if (attempts >= maxAttempts) {
            console.warn(
              "[page.tsx] Max attempts reached loading scheduling data from cache",
            );
          }
        }
      }, 500);

      return () => clearInterval(pollInterval);
    }
  }, [
    schedule.setBusinessHours,
    schedule.setHolidays,
    schedule.setEmployeeSchedules,
    schedule.setEmployeeScheduleExceptions,
    schedule.state.businessHours.length,
    schedule.state.holidays.length,
    schedule.state.employeeSchedules.length,
    schedule.state.employeeScheduleExceptions.length,
  ]);

  // Fetch workOrders when location changes, page changes, or search changes
  const fetchWorkOrders = useCallback(async (
    locationIds: number[],
    pageNumber: number = 1,
    searchString: string = "",
    woStatusIds?: string[],
    woDocStatusIds?: string[],
    segmentStatusFilters?: SegmentSchedulingStatus[],
  ) => {
    // Prevent concurrent fetch calls, but don't drop refreshes triggered while
    // a fetch is already running. The latest request should win after removals.
    if (isFetchingWorkOrdersRef.current) {
      pendingWorkOrdersFetchRef.current = {
        locationIds,
        pageNumber,
        searchString,
        woStatusIds,
        woDocStatusIds,
        segmentStatusFilters,
      };
      return;
    }

    isFetchingWorkOrdersRef.current = true;
    setLoadingWorkOrders(true);
    try {
      // Convert status IDs to numbers, filtering out "0" (All)
      const statusIds =
        woStatusIds && !woStatusIds.includes("0")
          ? woStatusIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))
          : undefined;
      const docStatusStrings =
        woDocStatusIds && !woDocStatusIds.includes("0")
          ? woDocStatusIds
          : undefined;
      const response = await SchedulingAPI.getWorkOrdersWithSegments(
        locationIds,
        searchString,
        pageNumber,
        15,
        statusIds,
        docStatusStrings,
        segmentStatusFilters,
      );

      // Convert API response to WorkOrder format
      const convertedWorkOrders: WorkOrder[] = response.workOrders.map(
        (wo: any) => ({
          id: wo.id || `wo-${wo.workOrderId || wo.invoiceDocId}`,
          workOrderNumber:
            wo.workOrderNumber || wo.invoiceDocId?.toString() || "",
          invoiceNumber: wo.invoiceNumber,
          name: wo.name || wo.description || "",
          company: wo.company || "",
          priority: wo.priority || "medium",
          category: wo.category || wo.serviceCode || "",
          // Capture WoStatusId from API response for status-based borderLeft coloring
          woStatusId:
            wo.woStatusId ||
            wo.WoStatusId ||
            wo.workOrderStatusId ||
            wo.WorkOrderStatusId ||
            undefined,
          // Include customer and work order details for display
          customerName: wo.customerName || "",
          customerNumber:
            wo.customerNumber || wo.CustomerNumber || wo.customerNo || "",
          myReference:
            wo.woDisplayText ||
            wo.WODisplayText ||
            wo.myReference ||
            "",
          description:
            wo.description || wo.woDescription || wo.WODescription || "",
          unitModel: wo.unitModel || wo.woUnitModel || wo.WOUnitModel || "",
          vinNumber:
            wo.vinNumber || wo.woUnitBaseSerial || wo.WOUnitBaseSerial || "",
          unitNumber: wo.unitNumber || wo.woUnitNo || wo.WOUnitNo || "",
          woPriorityId: wo.woPriorityId || wo.WOPriorityId || undefined,
          locationId: wo.locationId || wo.LocationId || undefined,
          status: wo.status ?? wo.Status ?? "",
          // CRITICAL: Normalize segments to ensure they have a 'name' field
          // API might return DisplayText, displayText, segmentName, or name
          segmentList: (wo.segments || wo.segmentList || []).map(mapSegmentFromApi),
        }),
      );

      schedule.setWorkOrders(convertedWorkOrders);
      setWorkOrderTotalCount(response.totalCount);
      setWorkOrderTotalPages(response.totalPages);
      setWorkOrderPage(response.pageNumber);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      setSnackbarMessage("Failed to fetch work orders");
      setSnackbarOpen(true);
    } finally {
      setLoadingWorkOrders(false);
      isFetchingWorkOrdersRef.current = false; // Reset flag after fetch completes
      const pendingFetch = pendingWorkOrdersFetchRef.current;
      if (pendingFetch) {
        pendingWorkOrdersFetchRef.current = null;
        fetchWorkOrders(
          pendingFetch.locationIds,
          pendingFetch.pageNumber,
          pendingFetch.searchString,
          pendingFetch.woStatusIds,
          pendingFetch.woDocStatusIds,
          pendingFetch.segmentStatusFilters,
        );
      }
    }
  }, [schedule.setWorkOrders]);

  const refreshWorkOrdersWithCurrentFilters = useCallback(() => {
    const query = workOrderQueryRef.current;
    fetchWorkOrders(
      query.locationIds,
      query.pageNumber,
      query.searchString,
      query.woStatusIds,
      query.woDocStatusIds,
      query.segmentSchedulingStatuses,
    );
  }, [fetchWorkOrders]);

  const debouncedFetchWorkOrders = useMemo(
    () =>
      debounce(
        (
          locationIds: number[],
          pageNumber: number,
          searchString: string,
          woStatusIds?: string[],
          woDocStatusIds?: string[],
          segmentStatusFilters?: SegmentSchedulingStatus[],
        ) => {
          fetchWorkOrders(
            locationIds,
            pageNumber,
            searchString,
            woStatusIds,
            woDocStatusIds,
            segmentStatusFilters,
          );
        },
        300,
      ),
    [fetchWorkOrders],
  );

  useEffect(() => {
    return () => {
      debouncedFetchWorkOrders.clear();
    };
  }, [debouncedFetchWorkOrders]);

  // Helper function to convert API response to ScheduledItem format
  const convertScheduleItemsToScheduledItems = (
    scheduleItemsData: any[],
  ): ScheduledItem[] => {
    return scheduleItemsData.map((item: any) => {
      // CRITICAL: Parse workDate correctly to avoid timezone issues
      // The API returns a date string (e.g., "2024-01-30" or "2024-01-30T00:00:00")
      // We need to parse it as a local date to prevent timezone shifts
      let workDate: Date;
      if (typeof item.workDate === "string") {
        // Parse date string and create local date (avoid UTC interpretation)
        // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss" formats
        const dateStr = item.workDate.split("T")[0]; // Get just the date part
        const [year, month, day] = dateStr.split("-").map(Number);
        workDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
        workDate.setHours(0, 0, 0, 0); // Ensure time is set to local midnight
      } else if (item.workDate instanceof Date) {
        workDate = new Date(item.workDate);
        workDate.setHours(0, 0, 0, 0); // Ensure time is set to local midnight
      } else {
        // Fallback: try to parse as-is
        workDate = new Date(item.workDate);
        workDate.setHours(0, 0, 0, 0);
      }

      // Convert WorkDate to day string using local date
      const dayOfWeek = workDate.getDay();
      const dayMap: { [key: number]: string } = {
        0: "sunday",
        1: "monday",
        2: "tuesday",
        3: "wednesday",
        4: "thursday",
        5: "friday",
        6: "saturday",
      };
      const day = dayMap[dayOfWeek] || "monday";

      // CRITICAL: Ensure displayText and description are always preserved
      // Priority: database value > API field variations > empty string
      const displayText =
        item.displayText ||
        item.DisplayText ||
        item.segmentDisplayText ||
        item.SegmentDisplayText ||
        "";
      const description =
        item.description ||
        item.Description ||
        item.segmentDescription ||
        item.SegmentDescription ||
        "";

      // CRITICAL: Capture Work Order Number from API response
      // This is used to display WO# in scheduled item tooltips
      const workOrderNumber =
        item.workOrderNumber ||
        item.WorkOrderNumber ||
        item.docNo ||
        item.DocNo ||
        "";

      // Capture Invoice Number from API response
      // This is used to display invoice number (e.g., "#129295") in scheduled items
      const invoiceNumber = item.invoiceNumber || item.InvoiceNumber || "";

      // Capture Work Order Status ID from API response
      // This is used to color the borderLeft with status color
      const woStatusId =
        item.woStatusId ||
        item.WoStatusId ||
        item.workOrderStatusId ||
        item.WorkOrderStatusId ||
        undefined;

      return {
        id: `scheduled-${item.scheduleItemId}`,
        scheduleItemId: item.scheduleItemId,
        segmentTaskId: item.segmentTaskId,
        workOrderId: item.workOrderId?.toString() || "",
        segmentId: item.segmentId?.toString(),
        segmentPartId: item.segmentPartId?.toString(),
        technicianId: item.technicianId,
        day: day as any,
        hours: Number(item.estimatedHours) || 0,
        order: 0, // Will be calculated from sortKey
        sortKey: Number(item.sortKey) || 0,
        workDate: workDate,
        overCapacity: item.overCapacity || false,
        technicianOff: item.technicianOff || false,
        // CRITICAL: Always preserve displayText and description from database
        displayText: displayText,
        description: description,
        // CRITICAL: Always preserve workOrderNumber from database for tooltip display
        workOrderNumber: workOrderNumber,
        // CRITICAL: Always preserve invoiceNumber from database for display
        invoiceNumber: invoiceNumber,
        // CRITICAL: Preserve woStatusId for status-based borderLeft coloring
        woStatusId: woStatusId,
        // New fields from InvoiceHeader
        customerName: item.customerName || item.CustomerName || "",
        customerNumber:
          item.customerNumber ||
          item.CustomerNumber ||
          item.customerNo ||
          item.CustomerNo ||
          "",
        myReference:
          item.woDisplayText ||
          item.WODisplayText ||
          item.myReference ||
          item.MyReference ||
          "",
        woDescription:
          item.woDescription || item.WoDescription || item.wODescription || "",
        unitModel: item.unitModel || item.UnitModel || "",
        vinNumber: item.vinNumber || item.VinNumber || "",
        unitNumber: item.unitNumber || item.UnitNumber || "",
        unitDescription: item.unitDescription || item.UnitDescription || "",
        woPriorityId: item.woPriorityId || item.WoPriorityId || undefined,
        taskStatus: item.taskStatus ?? item.TaskStatus,
        pctCompleted: item.pctCompleted ?? item.PctCompleted,
        isActive: item.isActive ?? item.IsActive,
      };
    });
  };

  const fetchHistoryScheduleItems = async (
    locationIds: number[],
    filter: "completed" | "archived",
    fromDate: Date,
    toDate: Date,
  ) => {
    const locationIdsForApi =
      locationIds.length > 0 ? locationIds : undefined;

    return SchedulingAPI.getCompletedAndArchivedItems({
      locationIds: locationIdsForApi,
      fromDate,
      toDate,
      filter,
    });
  };

  // Fetch scheduled items for Week view
  const fetchScheduledItemsForWeek = async (
    locationIds: number[],
    weekOffsetParam?: number,
  ) => {
    const requestId = ++scheduledItemsRequestSeqRef.current.week;
    setLoadingScheduledItems(true);
    try {
      // Use provided weekOffset or default to current week (0)
      const offset = weekOffsetParam !== undefined ? weekOffsetParam : 0;
      const { fromDate, toDate } = getWeekDateRange(offset);
      // Pass locationIds to API - empty array means "all locations"
      const locationIdsForApi =
        locationIds.length > 0 ? locationIds : undefined;

      const scheduleItemViewState =
        useTechnicianCalendarStore.getState().scheduleItemView;

      const scheduleItemsData =
        scheduleItemViewState === "active"
          ? await SchedulingAPI.getScheduleItems({
              locationIds: locationIdsForApi,
              fromDate,
              toDate,
            })
          : await fetchHistoryScheduleItems(
              locationIds,
              scheduleItemViewState,
              fromDate,
              toDate,
            );
      //debugger;
      // Convert API response to ScheduledItem format
      const convertedItems =
        convertScheduleItemsToScheduledItems(scheduleItemsData);

      // Cache per view
      scheduledItemsByViewRef.current.week = convertedItems;

      // Only apply to active view (prevents month/day responses overwriting week UI)
      if (
        activeViewModeRef.current === "week" &&
        scheduledItemsRequestSeqRef.current.week === requestId
      ) {
        schedule.setScheduledItems(convertedItems);
      }
    } catch (error) {
      console.error("Error fetching scheduled items for week:", error);
      setSnackbarMessage("Failed to fetch scheduled items");
      setSnackbarOpen(true);
    } finally {
      setLoadingScheduledItems(false);
    }
  };

  // Fetch scheduled items for Day view (separate API call)
  const fetchScheduledItemsForDay = async (
    locationIds: number[],
    workDate: Date,
  ) => {
    dayDateRef.current = workDate;
    const requestId = ++scheduledItemsRequestSeqRef.current.day;
    setLoadingScheduledItems(true);
    try {
      // Pass locationIds to API - empty array means "all locations"
      const locationIdsForApi =
        locationIds.length > 0 ? locationIds : undefined;

      const scheduleItemViewState =
        useTechnicianCalendarStore.getState().scheduleItemView;

      const dayStart = new Date(workDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(workDate);
      dayEnd.setHours(23, 59, 59, 999);

      const scheduleItemsData =
        scheduleItemViewState === "active"
          ? await SchedulingAPI.getScheduleItemsForDay({
              locationIds: locationIdsForApi,
              workDate,
            })
          : await fetchHistoryScheduleItems(
              locationIds,
              scheduleItemViewState,
              dayStart,
              dayEnd,
            );

      // Convert API response to ScheduledItem format
      const convertedItems =
        convertScheduleItemsToScheduledItems(scheduleItemsData);

      // Cache per view
      scheduledItemsByViewRef.current.day = convertedItems;

      // Only apply to active view
      if (
        activeViewModeRef.current === "day" &&
        scheduledItemsRequestSeqRef.current.day === requestId
      ) {
        schedule.setScheduledItems(convertedItems);
      }
    } catch (error) {
      console.error("Error fetching scheduled items for day:", error);
      setSnackbarMessage("Failed to fetch scheduled items for day");
      setSnackbarOpen(true);
    } finally {
      setLoadingScheduledItems(false);
    }
  };

  // Fetch scheduled items for Month view (separate API call)
  const fetchScheduledItemsForMonth = async (
    locationIds: number[],
    monthDate: Date,
  ) => {
    monthDateRef.current = monthDate;
    const requestId = ++scheduledItemsRequestSeqRef.current.month;
    setLoadingScheduledItems(true);
    try {
      // Pass locationIds to API - empty array means "all locations"
      const locationIdsForApi =
        locationIds.length > 0 ? locationIds : undefined;

      const monthStart = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth(),
        1,
      );
      const monthEnd = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth() + 1,
        0,
      );
      monthEnd.setHours(23, 59, 59, 999);

      const scheduleItemViewState =
        useTechnicianCalendarStore.getState().scheduleItemView;

      const scheduleItemsData =
        scheduleItemViewState === "active"
          ? await SchedulingAPI.getScheduleItemsForMonth({
              locationIds: locationIdsForApi,
              month: monthDate,
            })
          : await fetchHistoryScheduleItems(
              locationIds,
              scheduleItemViewState,
              monthStart,
              monthEnd,
            );

      // Convert API response to ScheduledItem format
      const convertedItems =
        convertScheduleItemsToScheduledItems(scheduleItemsData);

      // Cache per view
      scheduledItemsByViewRef.current.month = convertedItems;

      // Only apply to active view
      if (
        activeViewModeRef.current === "month" &&
        scheduledItemsRequestSeqRef.current.month === requestId
      ) {
        schedule.setScheduledItems(convertedItems);
      }
    } catch (error) {
      console.error("Error fetching scheduled items for month:", error);
      setSnackbarMessage("Failed to fetch scheduled items for month");
      setSnackbarOpen(true);
    } finally {
      setLoadingScheduledItems(false);
    }
  };

  // Legacy function name for backward compatibility (maps to Week view)
  const fetchScheduledItems = fetchScheduledItemsForWeek;

  const getLocationIdsForScheduleFetch = () =>
    selectedLocationIdsFromStore.includes("0")
      ? []
      : selectedLocationIdsFromStore
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));

  const refreshVisibleCalendar = async () => {
    const locationIdsToUse = getLocationIdsForScheduleFetch();
    if (activeViewModeRef.current === "day") {
      await fetchScheduledItemsForDay(locationIdsToUse, dayDateRef.current);
    } else if (activeViewModeRef.current === "month") {
      await fetchScheduledItemsForMonth(locationIdsToUse, monthDateRef.current);
    } else {
      await fetchScheduledItemsForWeek(locationIdsToUse, weekOffset);
    }
  };

  // Handle location change from CapacityBoard
  // CRITICAL: This ensures work orders and scheduled items are fetched on first load and location changes
  // locationIds: empty array means "All locations"
  const handleLocationChange = (locationIds: number[]) => {
    setSelectedLocationIds(locationIds);
    setWorkOrderPage(1);

    // Always fetch work orders (needed for display and drag-drop validation)
    if (!hasFetchedWorkOrdersRef.current) {
      hasFetchedWorkOrdersRef.current = true;
      fetchWorkOrders(
        locationIds,
        1,
        workOrderSearchString,
        selectedWoStatusIds,
        selectedWoDocStatusIds,
        segmentSchedulingStatuses,
      );
    } else {
      // If we've already fetched, this is a location change, so refetch
      debouncedFetchWorkOrders(
        locationIds,
        1,
        workOrderSearchString,
        selectedWoStatusIds,
        selectedWoDocStatusIds,
        segmentSchedulingStatuses,
      );
    }

    // Fetch scheduled items for the ACTIVE view only (prevents cross-tab coupling)
    if (!hasFetchedScheduledItemsRef.current) {
      if (initialDataLoaded) {
        hasFetchedScheduledItemsRef.current = true;
      } else {
        // initialDataLoaded useEffect will handle first fetch
        isInitialMountRef.current = false;
        return;
      }
    }

    if (activeViewModeRef.current === "day") {
      fetchScheduledItemsForDay(locationIds, dayDateRef.current);
    } else if (activeViewModeRef.current === "month") {
      fetchScheduledItemsForMonth(locationIds, monthDateRef.current);
    } else {
      fetchScheduledItemsForWeek(locationIds, weekOffset);
    }

    // Mark that initial mount is complete
    isInitialMountRef.current = false;
  };

  // Handle search change from WorkOrdersSidebar
  const handleSearchChange = (searchString: string) => {
    setWorkOrderSearchString(searchString);
    setWorkOrderPage(1); // Reset to page 1 when search changes
    fetchWorkOrders(
      selectedLocationIds,
      1,
      searchString,
      selectedWoStatusIds,
      selectedWoDocStatusIds,
      segmentSchedulingStatuses,
    );
  };

  const handleSegmentSchedulingStatusesChange = (
    statuses: SegmentSchedulingStatus[],
  ) => {
    setSegmentSchedulingStatuses(statuses);
    setWorkOrderPage(1);
    debouncedFetchWorkOrders(
      selectedLocationIds,
      1,
      workOrderSearchString,
      selectedWoStatusIds,
      selectedWoDocStatusIds,
      statuses,
    );
  };

  // Refetch work orders when WO status filter changes
  const prevWoStatusIdsRef = useRef<string[]>(selectedWoStatusIds);
  useEffect(() => {
    // Skip on initial mount - the location change handler will fetch initial data
    if (isInitialMountRef.current) {
      prevWoStatusIdsRef.current = selectedWoStatusIds;
      return;
    }
    // Only refetch if the status IDs have actually changed
    const prevIds = prevWoStatusIdsRef.current;
    const hasChanged =
      prevIds.length !== selectedWoStatusIds.length ||
      prevIds.some((id, i) => selectedWoStatusIds[i] !== id);
    if (hasChanged) {
      prevWoStatusIdsRef.current = selectedWoStatusIds;
      setWorkOrderPage(1);
      debouncedFetchWorkOrders(
        selectedLocationIds,
        1,
        workOrderSearchString,
        selectedWoStatusIds,
        selectedWoDocStatusIds,
        segmentSchedulingStatuses,
      );
    }
  }, [selectedWoStatusIds]);

  const prevWoDocStatusIdsRef = useRef<string[]>(selectedWoDocStatusIds);
  useEffect(() => {
    if (isInitialMountRef.current) {
      prevWoDocStatusIdsRef.current = selectedWoDocStatusIds;
      return;
    }
    const prevIds = prevWoDocStatusIdsRef.current;
    const hasChanged =
      prevIds.length !== selectedWoDocStatusIds.length ||
      prevIds.some((id, i) => selectedWoDocStatusIds[i] !== id);
    if (hasChanged) {
      prevWoDocStatusIdsRef.current = selectedWoDocStatusIds;
      setWorkOrderPage(1);
      debouncedFetchWorkOrders(
        selectedLocationIds,
        1,
        workOrderSearchString,
        selectedWoStatusIds,
        selectedWoDocStatusIds,
        segmentSchedulingStatuses,
      );
    }
  }, [selectedWoDocStatusIds]);

  // Handler for opening a work order from scheduled items (browser mode only)
  const handleOpenWorkOrder = (invoiceDocId: number) => {
    setDialogWorkOrderId(invoiceDocId);
    setShowWorkOrderDialog(true);
  };

  // Handler for hiding the work order dialog
  const showHideWorkOrderPopup = (isPopupToShow: boolean) => {
    setShowWorkOrderDialog(isPopupToShow);
    if (!isPopupToShow) {
      setDialogWorkOrderId(null);

      // Full module reload after closing the Work Order dialog.
      // Some data is cached in stores/refs; a reload guarantees the latest state everywhere.
      window.location.reload();
    }
  };

  // Calculate today's scheduled items count for the alert banner
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayItems = schedule.state.scheduledItems.filter((item) => {
      if (!item.workDate) return false;
      const itemDate = new Date(item.workDate);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate.getTime() === today.getTime();
    });

    setTodayItemsCount(todayItems.length);
  }, [schedule.state.scheduledItems]);

  // Handler to navigate to Day view when clicking on the alert banner
  const handleViewTodayClick = () => {
    setTodayAlertDismissed(true);
    // Increment to trigger Day view in CapacityBoard
    setTriggerDayView((prev) => prev + 1);
  };

  // Track if initial data load is complete (for capacity calculations)
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [hasFetchedScheduledItems, setHasFetchedScheduledItems] =
    useState(false);

  // Use refs to track if initial fetch has been done to prevent duplicate calls
  const hasFetchedWorkOrdersRef = useRef(false);
  const hasFetchedScheduledItemsRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const isFetchingWorkOrdersRef = useRef(false); // Prevent concurrent fetch calls
  const pendingWorkOrdersFetchRef = useRef<{
    locationIds: number[];
    pageNumber: number;
    searchString: string;
    woStatusIds?: string[];
    woDocStatusIds?: string[];
    segmentStatusFilters?: SegmentSchedulingStatus[];
  } | null>(null);
  const isResettingRef = useRef(false); // Track when reset is in progress to prevent duplicate API calls

  /**
   * Show CapacityBoard once technicians and locations are available.
   * Business hours, employee schedules, exceptions, and holidays can be empty
   * (common on customer DBs with no store-hours setup). Treating those as
   * required kept initialDataLoaded false forever, so the board never mounted
   * even when every API returned 200 with [].
   */
  useEffect(() => {
    const hasTechnicians = schedule.state.technicians.length > 0;
    const hasLocations = schedule.state.locations.length > 0;

    if (hasTechnicians && hasLocations && !initialDataLoaded) {
      setInitialDataLoaded(true);
    }
  }, [
    schedule.state.technicians.length,
    schedule.state.locations.length,
    initialDataLoaded,
  ]);

  // If lookups hang or return empty technicians/locations, still show the board
  // rather than leaving a blank center panel with no error.
  useEffect(() => {
    if (initialDataLoaded) return;
    const timeoutId = window.setTimeout(() => {
      setInitialDataLoaded(true);
    }, 10000);
    return () => window.clearTimeout(timeoutId);
  }, [initialDataLoaded]);

  // Note: Initial fetch is handled by handleLocationChange when CapacityBoard calls it on mount
  // No need for a separate useEffect here to avoid duplicate calls

  // Fetch scheduled items once required data is loaded (fallback if handleLocationChange didn't fetch yet)
  // CRITICAL: This ensures scheduled items are ALWAYS loaded on page refresh or first load
  // Scheduled items are independent of work orders and MUST be loaded for capacity calculations
  // CRITICAL: Use weekOffset from store to fetch data for the selected week (not always current week)
  // CRITICAL: Only fetch on initial load, not when selectedLocationId changes (handleLocationChange handles that)
  // CRITICAL: Only fetch for Week view on initial load - Day and Month views fetch when user switches tabs
  useEffect(() => {
    // Fetch scheduled items when:
    // 1. Data is loaded (technicians, businessHours, locations) - REQUIRED for capacity calculations
    // 2. We haven't fetched scheduled items yet
    // 3. View mode is "week" (default) - Day and Month views fetch separately when user switches tabs
    // This handles both first load and page refresh scenarios
    // Note: We don't wait for work orders because scheduled items are independent
    // CRITICAL: Use weekOffset from store so refresh loads the correct week's data
    // CRITICAL: Only fetch if handleLocationChange hasn't already fetched (to prevent duplicate calls)
    // CRITICAL: Use selectedLocationIds from store (prefer store over local state) to ensure API always has locationIds
    // Note: viewMode is now local state in CapacityBoard, so we always fetch week data on initial load
    if (initialDataLoaded && !hasFetchedScheduledItemsRef.current) {
      hasFetchedScheduledItemsRef.current = true;
      // Convert store's string array to number array, filtering out "0" (All)
      const locationIdsToUse = selectedLocationIdsFromStore.includes("0")
        ? []
        : selectedLocationIdsFromStore
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id));
      // Use weekOffset from store (may not be 0 if user was on a different week)
      fetchScheduledItemsForWeek(locationIdsToUse, weekOffset);
      setHasFetchedScheduledItems(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoaded]); // Removed viewMode - it's now local state in CapacityBoard

  // REMOVED: viewMode-based useEffects for Day/Week/Month tab switching
  // viewMode is now local state in CapacityBoard (following WorkOrderDialogContainerMain pattern)
  // This prevents cascading API calls on every tab switch for instant tab switching
  // API calls are now triggered via callbacks (onDayViewDateChange) when needed

  /**
   * FETCH SCHEDULED ITEMS ON WEEK CHANGE
   *
   * When the user navigates to a different week (next/previous buttons or date picker),
   * we need to reload scheduled items for that week's date range.
   *
   * This ensures technician cards show the correct scheduled items for the selected week.
   *
   * CRITICAL: Always use selectedLocationIds from store (not null) to ensure API call has locationIds
   * CRITICAL: Skip fetch if a reset is in progress (onResetFilters handles that fetch)
   */
  useEffect(() => {
    // Skip fetch if reset is in progress - onResetFilters callback handles the fetch
    if (isResettingRef.current) {
      isResettingRef.current = false; // Clear the flag for next time
      return;
    }

    // Only fetch if we have initial data loaded
    // CRITICAL: Use selectedLocationIds from store (prefer store over local state)
    // This ensures we always have locationIds (empty array if "All" is selected)
    if (initialDataLoaded) {
      // Convert store's string array to number array, filtering out "0" (All)
      const locationIdsToUse = selectedLocationIdsFromStore.includes("0")
        ? []
        : selectedLocationIdsFromStore
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id));
      fetchScheduledItems(locationIdsToUse, weekOffset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, selectedLocationIdsFromStore]);

  useEffect(() => {
    if (!initialDataLoaded || isInitialMountRef.current) {
      return;
    }

    const locationIdsToUse = selectedLocationIdsFromStore.includes("0")
      ? []
      : selectedLocationIdsFromStore
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));

    if (activeViewModeRef.current === "day") {
      fetchScheduledItemsForDay(locationIdsToUse, dayDateRef.current);
    } else if (activeViewModeRef.current === "month") {
      fetchScheduledItemsForMonth(locationIdsToUse, monthDateRef.current);
    } else {
      fetchScheduledItemsForWeek(locationIdsToUse, weekOffset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleItemView]);

  // Find selected segment and its parent work order
  // CRITICAL: Find segment from workOrders OR from dragData (for search results)
  // This ensures the break modal works for both segments in current page and from search results
  const selectedSegment = selectedWorkOrderId
    ? (() => {
        // First, try to find in current workOrders
        for (const wo of schedule.state.workOrders) {
          const segment = wo.segmentList.find(
            (seg) => seg.id === selectedWorkOrderId,
          );
          if (segment) {
            return { segment, workOrder: wo };
          }
        }

        // If not found, check if we have it from dragData (search results)
        if (selectedSegmentFromDrag) {
          return selectedSegmentFromDrag;
        }

        return null;
      })()
    : null;

  // NOTE: isBroken functionality has been disabled to allow segments to be moved/broken multiple times
  // without requiring a page refresh. All segments are now always draggable and clickable.
  const isBrokenMap = new Map(
    schedule.state.workOrders.map((wo) => [
      wo.id,
      false, // Always allow dragging/breaking - was: schedule.isWorkOrderBroken(wo.id)
    ]),
  );

  const handleBreakSegment = (parts: number[]) => {
    if (selectedWorkOrderId) {
      // selectedWorkOrderId is now actually a segmentId
      schedule.breakSegment(selectedWorkOrderId, parts);
      setCapacityWarning(null);
      // Auto-open right sidebar when breaking down segments
      setRightSidebarCollapsed(false);
    }
  };

  const handleGoOverCapacity = () => {
    if (!overrideRequest) return;
    const { dragData, techId, day, position, reason, date: storedDate } =
      overrideRequest;
    // Prefer the date captured when the drop opened this dialog (visible column).
    // Fall back to weekOffset only if date was not stored.
    const date = storedDate || getDateForDayWithOffset(day, weekOffset);
    schedule.scheduleFromDrag(
      dragData,
      techId,
      day,
      dragData.hours,
      position,
      {
        overCapacity: reason === "over-capacity",
        technicianOff: reason === "tech-off",
      },
      date,
    );
    setOverrideRequest(null);
    setBreakModalOpen(false);
  };

  const handleSegmentClick = (segmentId: string) => {
    // Find the segment to check its estimated hours
    let segment: any = null;
    for (const wo of schedule.state.workOrders) {
      const found = wo.segmentList.find((seg) => seg.id === segmentId);
      if (found) {
        segment = found;
        break;
      }
    }

    // Check if segment has 0 estimated hours
    const estimatedHours = segment?.estimatedHours ?? segment?.hours ?? 0;
    if (estimatedHours === 0) {
      setSnackbarMessage(
        "You cannot break this segment as it has 0 estimated hours. Please update hours first.",
      );
      setSnackbarOpen(true);
      return;
    }

    // Open break segment modal for the selected segment
    setSelectedWorkOrderId(segmentId);
    setCapacityWarning(null);
    setBreakModalOpen(true);
  };

  const handleDropOnCard =
    (techId: number, day: string, passedDate?: Date) =>
    async (e: React.DragEvent) => {
      try {
        let data = e.dataTransfer.getData("application/json");
        if (!data) {
          data = e.dataTransfer.getData("text/plain");
        }
        const dragData = data ? JSON.parse(data) : null;
        if (!dragData) return;

        // Validate that items have non-zero estimated hours before allowing drop
        // For work orders, check totalHours or sum of segment hours
        if (dragData.type === "work-order") {
          const totalHours =
            dragData.totalHours ??
            (dragData.segments?.reduce(
              (sum: number, seg: any) => sum + (seg.estimatedHours || 0),
              0,
            ) ||
              0);
          if (totalHours === 0) {
            setSnackbarMessage(
              "Please change the estimated hours first. Work order has 0 hours.",
            );
            setSnackbarOpen(true);
            return;
          }
        }
        // For segments, check hours
        if (
          dragData.type === "segment" &&
          (dragData.hours === 0 || dragData.hours === undefined)
        ) {
          setSnackbarMessage(
            "Please change the estimated hours first. Segment has 0 hours.",
          );
          setSnackbarOpen(true);
          return;
        }
        // For segment parts, check hours
        if (
          dragData.type === "segment-part" &&
          (dragData.hours === 0 || dragData.hours === undefined)
        ) {
          setSnackbarMessage(
            "Please change the estimated hours first. Segment part has 0 hours.",
          );
          setSnackbarOpen(true);
          return;
        }

        // Handle moving grouped scheduled items between cards
        if (dragData.type === "scheduled-item-group" && Array.isArray(dragData.itemIds)) {
          const sourceTechId = dragData.sourceTechnicianId;
          const sourceDay = dragData.sourceDay;
          if (sourceTechId === techId && sourceDay === day) {
            return; // Same location — reorder handled by card
          }

          const tech = schedule.state.technicians.find(
            (t) => t.identity === techId,
          );
          if (!tech) return;

          const date = passedDate || getDateForDayWithOffset(day, weekOffset);
          const dayOfWeek = getDayOfWeek(day);

          // Check if location is closed
          const locationIds = [tech.defaultLocationIdentity];
          const isLocationClosed =
            !isLocationOpen(
              dayOfWeek,
              locationIds,
              schedule.state.businessHours,
            ) || isHoliday(date, locationIds, schedule.state.holidays);
          if (isLocationClosed) {
            setSnackbarMessage(
              `Location is closed on ${capitalizeFirstLetter(day)}`,
            );
            setSnackbarOpen(true);
            return;
          }

          // Move all items in the group (append when no position-based drop)
          for (const itemId of dragData.itemIds) {
            await schedule.moveScheduledItem(itemId, techId, day, date);
          }
          return;
        }

        // Handle moving scheduled items between cards
        if (dragData.type === "scheduled-item" && dragData.scheduledItemId) {
          // Use drag data for same-location check instead of looking up stale state.
          // TechnicianCard's custom memo ignores function props, so the drop handler
          // closure can capture outdated schedule.state where the item doesn't exist yet.
          const sourceTechId = dragData.sourceTechnicianId;
          const sourceDay = dragData.sourceDay;
          if (sourceTechId === techId && sourceDay === day) {
            return; // Same location — let reorder handle this
          }

          const tech = schedule.state.technicians.find(
            (t) => t.identity === techId,
          );
          if (!tech) return;

          // CRITICAL: Use passedDate if provided (from Day view), otherwise calculate from weekOffset
          const date = passedDate || getDateForDayWithOffset(day, weekOffset);

          // Past-due items from search may only land on today or a future date
          if (dragData.isPastDueReschedule && !isDateTodayOrFuture(date)) {
            setSnackbarMessage(
              "Past due items can only be moved to today or a future date.",
            );
            setSnackbarOpen(true);
            return;
          }

          const dayOfWeek = getDayOfWeek(day);

          // Check if location is closed
          const locationIds = [tech.defaultLocationIdentity];
          const isLocationClosed =
            !isLocationOpen(
              dayOfWeek,
              locationIds,
              schedule.state.businessHours,
            ) || isHoliday(date, locationIds, schedule.state.holidays);
          if (isLocationClosed) {
            setSnackbarMessage(
              `Location is closed on ${capitalizeFirstLetter(day)}`,
            );
            setSnackbarOpen(true);
            return;
          }

          const itemIds: string[] =
            Array.isArray(dragData.scheduledItemIds) &&
            dragData.scheduledItemIds.length > 0
              ? dragData.scheduledItemIds
              : [dragData.scheduledItemId];

          const pastDueSeed = dragData.isPastDueReschedule
            ? {
                hours: dragData.hours,
                workOrderId: dragData.workOrderId,
                segmentId: dragData.segmentId,
                displayText: dragData.segmentName,
                description: dragData.description,
                workOrderNumber: dragData.workOrderNumber,
                invoiceNumber: dragData.invoiceNumber,
                customerName: dragData.customerName,
                customerNumber: dragData.customerNumber,
                woStatusId: dragData.woStatusId,
                woDescription: dragData.woDescription,
                woPriorityId: dragData.woPriorityId,
                unitNumber: dragData.unitNumber,
                unitModel: dragData.unitModel,
                vinNumber: dragData.vinNumber,
                myReference: dragData.myReference,
              }
            : undefined;

          let anyMoved = false;
          for (const itemId of itemIds) {
            const moved = await schedule.moveScheduledItem(
              itemId,
              techId,
              day,
              date,
              pastDueSeed,
            );
            anyMoved = anyMoved || moved;
          }

          // Past-due from search is often not on the current board week — refresh after
          // the API update completes so the board stays in sync (optimistic insert already shown).
          if (anyMoved && dragData.isPastDueReschedule) {
            const locationIdsToUse = selectedLocationIdsFromStore.includes("0")
              ? []
              : selectedLocationIdsFromStore
                  .map((id) => Number(id))
                  .filter((id) => !isNaN(id));
            await fetchScheduledItemsForWeek(locationIdsToUse, weekOffset);
            refreshWorkOrdersWithCurrentFilters();
          }
          return;
        }

        // Handle new items from sidebar (work-order or segment-part)
        const tech = schedule.state.technicians.find(
          (t) => t.identity === techId,
        );
        if (!tech) return;

        // LOCATION VALIDATION: Check if work order location matches technician location
        // Get work order location from dragData or look it up from workOrders
        let workOrderLocationId: number | undefined;
        if (
          dragData.type === "work-order" ||
          dragData.type === "segment" ||
          dragData.type === "segment-part"
        ) {
          // First try to get from dragData
          workOrderLocationId = dragData.locationId;

          // If not in dragData, look up from workOrders
          if (!workOrderLocationId && dragData.workOrderId) {
            const workOrder = schedule.state.workOrders.find(
              (wo) => wo.id === dragData.workOrderId,
            );
            workOrderLocationId = workOrder?.locationId;
          }

          // Validate location match
          // if (workOrderLocationId && tech.defaultLocationIdentity &&
          //     workOrderLocationId !== tech.defaultLocationIdentity) {
          //   setSnackbarMessage(
          //     "Work order segment should belong to the same location as the technician.",
          //   );
          //   setSnackbarOpen(true);
          //   return;
          // }
        }

        // CRITICAL: Use passedDate if provided (from Day view), otherwise calculate from weekOffset
        // This ensures workDate is set correctly for both Week and Day views
        const date = passedDate || getDateForDayWithOffset(day, weekOffset);
        const dayOfWeek = getDayOfWeek(day);

        // Check if location is closed
        const locationIds = [tech.defaultLocationIdentity];
        const isLocationClosed =
          !isLocationOpen(
            dayOfWeek,
            locationIds,
            schedule.state.businessHours,
          ) || isHoliday(date, locationIds, schedule.state.holidays);
        if (isLocationClosed) {
          setSnackbarMessage(
            `Location is closed on ${
              day.charAt(0).toUpperCase() + day.slice(1)
            }`,
          );
          setSnackbarOpen(true);
          return;
        }

        // Allow scheduling even if technician is off

        const available = schedule.getAvailableHours(techId, day, date);
        const requested = dragData.hours;

        // Handle work-order type: schedule all segments at once
        // For work orders, we allow dropping regardless of capacity (no capacity check)
        if (
          dragData.type === "work-order" &&
          dragData.segments &&
          dragData.segments.length > 0
        ) {
          // Determine if this will be over capacity (for flagging purposes only)
          const isOverCapacity = requested > available;

          // Schedule all segments from the work order (allow even if over capacity)
          // Pass work order details from dragData for display in scheduled items
          schedule.scheduleWorkOrder(
            dragData.workOrderId || dragData.id,
            dragData.segments,
            techId,
            day,
            date,
            { overCapacity: isOverCapacity },
            {
              workOrderNumber: dragData.workOrderNumber,
              customerName: dragData.customerName,
              customerNumber: dragData.customerNumber,
              myReference: dragData.myReference,
              woStatusId: dragData.woStatusId,
            },
          );
          return;
        }

        // Check capacity first - if insufficient, handle accordingly
        // For segments (not work orders), show break modal when capacity is exceeded
        if (requested > available) {
          // For segments that exceed capacity, show break modal
          // This works for both segments in current workOrders and segments from search results
          if (dragData.type === "segment") {
            // Try to find the segment and its parent work order in current workOrders
            // Note: Segment might not be found if it's from search results (paginated)
            let foundSegment: Segment | null = null;
            for (const wo of schedule.state.workOrders) {
              const segment = wo.segmentList.find(
                (seg) => seg.id === dragData.id,
              );
              if (segment) {
                foundSegment = segment;
                break;
              }
            }

            // Always show break modal for segments that exceed capacity
            // Use dragData directly if segment not found in current workOrders (search results)
            setSelectedWorkOrderId(foundSegment?.id || dragData.id);

            // CRITICAL: If segment not found in current workOrders, create a temporary segment from dragData
            // This ensures the modal displays correctly for segments from search results
            if (!foundSegment) {
              // Try to find work order name from current workOrders array
              const workOrderName =
                schedule.state.workOrders.find(
                  (wo) => wo.id === dragData.workOrderId,
                )?.name || "Work Order";

              // CRITICAL: Get segment name from dragData with fallbacks
              // dragData might have segmentName, name, DisplayText, or displayText
              const segmentName =
                dragData.segmentName ||
                dragData.name ||
                dragData.DisplayText ||
                dragData.displayText ||
                `Segment ${dragData.id}`;

              // Create a temporary segment and work order from dragData for the modal
              const tempSegment: Segment = {
                id: dragData.id,
                workOrderId: dragData.workOrderId || "",
                name: segmentName,
                hours: dragData.hours || 0,
                estimatedHours: dragData.hours || 0,
                segmentParts: [],
              };
              const tempWorkOrder: WorkOrder = {
                id: dragData.workOrderId || "",
                workOrderNumber: "",
                name: workOrderName,
                invoiceNumber: "",
                company: "",
                priority: "medium",
                category: "",
                segmentList: [],
              };
              setSelectedSegmentFromDrag({
                segment: tempSegment,
                workOrder: tempWorkOrder,
              });
            } else {
              setSelectedSegmentFromDrag(null);
            }

            setCapacityWarning({
              technicianName: tech.userName,
              day: capitalizeFirstLetter(day),
              availableHours: available,
              requestedHours: requested,
            });
            setOverrideRequest({
              dragData,
              techId,
              day,
              date, // CRITICAL: Pass date for correct workDate calculation
              reason: "over-capacity",
              techName: tech?.userName || "Technician",
            });
            setBreakModalOpen(true);
            return;
          } else {
            // For non-segments, prevent drop if over capacity
            setOverrideRequest({
              dragData,
              techId,
              day,
              date, // CRITICAL: Pass date for correct workDate calculation
              reason: "over-capacity",
              techName: tech?.userName || "Technician",
            });
            return;
          }
        }

        // If capacity is sufficient, allow the drop (even if segment not found in current workOrders)
        // This handles segments from search results that aren't in the current page
        // CRITICAL: Pass the date to ensure workDate is set correctly for the selected week
        // Schedule the item at the end
        schedule.scheduleFromDrag(
          dragData,
          techId,
          day,
          requested,
          undefined,
          undefined,
          date,
        );
      } catch (error) {
        console.error(" Failed to parse drag data:", error);
      }
    };

  const handleDropAtPosition =
    (techId: number, day: string, passedDate?: Date) =>
    async (e: React.DragEvent, position: number) => {
      try {
        let data = e.dataTransfer.getData("application/json");
        if (!data) {
          data = e.dataTransfer.getData("text/plain");
        }
        const dragData = data ? JSON.parse(data) : null;
        if (!dragData) return;

        // Validate that items have non-zero estimated hours before allowing drop
        // For work orders, check totalHours or sum of segment hours
        if (dragData.type === "work-order") {
          const totalHours =
            dragData.totalHours ??
            (dragData.segments?.reduce(
              (sum: number, seg: any) => sum + (seg.estimatedHours || 0),
              0,
            ) ||
              0);
          if (totalHours === 0) {
            setSnackbarMessage(
              "Please change the estimated hours first. Work order has 0 hours.",
            );
            setSnackbarOpen(true);
            return;
          }
        }
        // For segments, check hours
        if (
          dragData.type === "segment" &&
          (dragData.hours === 0 || dragData.hours === undefined)
        ) {
          setSnackbarMessage(
            "Please change the estimated hours first. Segment has 0 hours.",
          );
          setSnackbarOpen(true);
          return;
        }
        // For segment parts, check hours
        if (
          dragData.type === "segment-part" &&
          (dragData.hours === 0 || dragData.hours === undefined)
        ) {
          setSnackbarMessage(
            "Please change the estimated hours first. Segment part has 0 hours.",
          );
          setSnackbarOpen(true);
          return;
        }

        // Handle scheduled-item-group moves between cards
        if (dragData.type === "scheduled-item-group" && Array.isArray(dragData.itemIds)) {
          const sourceTechId = dragData.sourceTechnicianId;
          const sourceDay = dragData.sourceDay;
          if (sourceTechId === techId && sourceDay === day) {
            return; // Same location — reorder handled by card
          }

          const tech = schedule.state.technicians.find(
            (t) => t.identity === techId,
          );
          if (!tech) return;

          const date = passedDate || getDateForDayWithOffset(day, weekOffset);
          const dayOfWeek = getDayOfWeek(day);

          // Check if location is closed
          const locationIds = [tech.defaultLocationIdentity];
          const isLocationClosed =
            !isLocationOpen(
              dayOfWeek,
              locationIds,
              schedule.state.businessHours,
            ) || isHoliday(date, locationIds, schedule.state.holidays);
          if (isLocationClosed) {
            setSnackbarMessage(
              `Location is closed on ${capitalizeFirstLetter(day)}`,
            );
            setSnackbarOpen(true);
            return;
          }

          // Move all items in the group at the requested position
          let insertPosition = position;
          for (const itemId of dragData.itemIds) {
            await schedule.moveScheduledItem(
              itemId,
              techId,
              day,
              date,
              undefined,
              insertPosition,
            );
            insertPosition += 1;
          }
          return;
        }

        // Handle moving scheduled items between cards
        if (dragData.type === "scheduled-item" && dragData.scheduledItemId) {
          // Use drag data for same-location check instead of looking up stale state.
          const sourceTechId = dragData.sourceTechnicianId;
          const sourceDay = dragData.sourceDay;
          if (sourceTechId === techId && sourceDay === day) {
            return; // Same location — let reorder handle this
          }

          const tech = schedule.state.technicians.find(
            (t) => t.identity === techId,
          );
          if (!tech) return;

          // CRITICAL: Use passedDate if provided (from Day view), otherwise calculate from weekOffset
          const date = passedDate || getDateForDayWithOffset(day, weekOffset);

          if (dragData.isPastDueReschedule && !isDateTodayOrFuture(date)) {
            setSnackbarMessage(
              "Past due items can only be moved to today or a future date.",
            );
            setSnackbarOpen(true);
            return;
          }

          const dayOfWeek = getDayOfWeek(day);

          // Check if location is closed
          const locationIds = [tech.defaultLocationIdentity];
          const isLocationClosed =
            !isLocationOpen(
              dayOfWeek,
              locationIds,
              schedule.state.businessHours,
            ) || isHoliday(date, locationIds, schedule.state.holidays);
          if (isLocationClosed) {
            setSnackbarMessage(
              `Location is closed on ${capitalizeFirstLetter(day)}`,
            );
            setSnackbarOpen(true);
            return;
          }

          const itemIds: string[] =
            Array.isArray(dragData.scheduledItemIds) &&
            dragData.scheduledItemIds.length > 0
              ? dragData.scheduledItemIds
              : [dragData.scheduledItemId];

          const pastDueSeed = dragData.isPastDueReschedule
            ? {
                hours: dragData.hours,
                workOrderId: dragData.workOrderId,
                segmentId: dragData.segmentId,
                displayText: dragData.segmentName,
                description: dragData.description,
                workOrderNumber: dragData.workOrderNumber,
                invoiceNumber: dragData.invoiceNumber,
                customerName: dragData.customerName,
                customerNumber: dragData.customerNumber,
                woStatusId: dragData.woStatusId,
                woDescription: dragData.woDescription,
                woPriorityId: dragData.woPriorityId,
                unitNumber: dragData.unitNumber,
                unitModel: dragData.unitModel,
                vinNumber: dragData.vinNumber,
                myReference: dragData.myReference,
              }
            : undefined;

          let anyMoved = false;
          let insertPosition = position;
          for (const itemId of itemIds) {
            const moved = await schedule.moveScheduledItem(
              itemId,
              techId,
              day,
              date,
              pastDueSeed,
              insertPosition,
            );
            insertPosition += 1;
            anyMoved = anyMoved || moved;
          }

          if (anyMoved && dragData.isPastDueReschedule) {
            const locationIdsToUse = selectedLocationIdsFromStore.includes("0")
              ? []
              : selectedLocationIdsFromStore
                  .map((id) => Number(id))
                  .filter((id) => !isNaN(id));
            await fetchScheduledItemsForWeek(locationIdsToUse, weekOffset);
            refreshWorkOrdersWithCurrentFilters();
          }
          return;
        }

        // Handle work-order, segment, and segment-part types for position-based drops
        if (
          dragData.type !== "work-order" &&
          dragData.type !== "segment" &&
          dragData.type !== "segment-part"
        ) {
          return;
        }

        const tech = schedule.state.technicians.find(
          (t) => t.identity === techId,
        );
        if (!tech) return;

        // LOCATION VALIDATION: Check if work order location matches technician location
        // Get work order location from dragData or look it up from workOrders
        let workOrderLocationId: number | undefined;
        if (
          dragData.type === "work-order" ||
          dragData.type === "segment" ||
          dragData.type === "segment-part"
        ) {
          // First try to get from dragData
          workOrderLocationId = dragData.locationId;

          // If not in dragData, look up from workOrders
          if (!workOrderLocationId && dragData.workOrderId) {
            const workOrder = schedule.state.workOrders.find(
              (wo) => wo.id === dragData.workOrderId,
            );
            workOrderLocationId = workOrder?.locationId;
          }

          // Validate location match
          // if (
          //   workOrderLocationId &&
          //   tech.defaultLocationIdentity &&
          //   workOrderLocationId !== tech.defaultLocationIdentity
          // ) {
          //   setSnackbarMessage(
          //     "Work order segment should belong to the same location as the technician.",
          //   );
          //   setSnackbarOpen(true);
          //   return;
          // }
        }

        // CRITICAL: Use passedDate if provided (from Day view), otherwise calculate from weekOffset
        // This ensures workDate is set correctly for both Week and Day views
        const date = passedDate || getDateForDayWithOffset(day, weekOffset);
        const dayOfWeek = getDayOfWeek(day);

        // Check if location is closed
        const locationIds = [tech.defaultLocationIdentity];
        const isLocationClosed =
          !isLocationOpen(
            dayOfWeek,
            locationIds,
            schedule.state.businessHours,
          ) || isHoliday(date, locationIds, schedule.state.holidays);
        if (isLocationClosed) {
          setSnackbarMessage(
            `Location is closed on ${
              day.charAt(0).toUpperCase() + day.slice(1)
            }`,
          );
          setSnackbarOpen(true);
          return;
        }

        // Allow scheduling even if technician is off

        const available = schedule.getAvailableHours(techId, day, date);
        const requested = dragData.hours;

        // Handle work-order type: schedule all segments at once
        // For work orders, we allow dropping regardless of capacity (no capacity check)
        if (
          dragData.type === "work-order" &&
          dragData.segments &&
          dragData.segments.length > 0
        ) {
          // Determine if this will be over capacity (for flagging purposes only)
          const isOverCapacity = requested > available;

          // Schedule all segments from the work order (allow even if over capacity)
          // Pass work order details from dragData for display in scheduled items
          schedule.scheduleWorkOrder(
            dragData.workOrderId || dragData.id,
            dragData.segments,
            techId,
            day,
            date,
            { overCapacity: isOverCapacity },
            {
              workOrderNumber: dragData.workOrderNumber,
              customerName: dragData.customerName,
              customerNumber: dragData.customerNumber,
              myReference: dragData.myReference,
              woStatusId: dragData.woStatusId,
            },
          );
          return;
        }

        // Check capacity first - if insufficient, handle accordingly
        // For segments (not work orders), show break modal when capacity is exceeded
        if (requested > available) {
          // For segments that exceed capacity, show break modal
          // This works for both segments in current workOrders and segments from search results
          if (dragData.type === "segment") {
            // Try to find the segment and its parent work order in current workOrders
            // Note: Segment might not be found if it's from search results (paginated)
            let foundSegment: Segment | null = null;
            for (const wo of schedule.state.workOrders) {
              const segment = wo.segmentList.find(
                (seg) => seg.id === dragData.id,
              );
              if (segment) {
                foundSegment = segment;
                break;
              }
            }

            // Always show break modal for segments that exceed capacity
            // Use dragData directly if segment not found in current workOrders (search results)
            setSelectedWorkOrderId(foundSegment?.id || dragData.id);

            // CRITICAL: If segment not found in current workOrders, create a temporary segment from dragData
            // This ensures the modal displays correctly for segments from search results
            if (!foundSegment) {
              // Try to find work order name from current workOrders array
              const workOrderName =
                schedule.state.workOrders.find(
                  (wo) => wo.id === dragData.workOrderId,
                )?.name || "Work Order";

              // CRITICAL: Get segment name from dragData with fallbacks
              // dragData might have segmentName, name, DisplayText, or displayText
              const segmentName =
                dragData.segmentName ||
                dragData.name ||
                dragData.DisplayText ||
                dragData.displayText ||
                `Segment ${dragData.id}`;

              // Create a temporary segment and work order from dragData for the modal
              const tempSegment: Segment = {
                id: dragData.id,
                workOrderId: dragData.workOrderId || "",
                name: segmentName,
                hours: dragData.hours || 0,
                estimatedHours: dragData.hours || 0,
                segmentParts: [],
              };
              const tempWorkOrder: WorkOrder = {
                id: dragData.workOrderId || "",
                workOrderNumber: "",
                invoiceNumber: "",
                name: workOrderName,
                company: "",
                priority: "medium",
                category: "",
                segmentList: [],
              };
              setSelectedSegmentFromDrag({
                segment: tempSegment,
                workOrder: tempWorkOrder,
              });
            } else {
              setSelectedSegmentFromDrag(null);
            }

            setCapacityWarning({
              technicianName: tech.userName,
              day: capitalizeFirstLetter(day),
              availableHours: available,
              requestedHours: requested,
            });
            setOverrideRequest({
              dragData,
              techId,
              day,
              date, // CRITICAL: Pass date for correct workDate calculation
              position,
              reason: "over-capacity",
              techName: tech?.userName || "Technician",
            });
            setBreakModalOpen(true);
            return;
          } else {
            // For non-segments, prevent drop if over capacity
            setOverrideRequest({
              dragData,
              techId,
              day,
              date, // CRITICAL: Pass date for correct workDate calculation
              position,
              reason: "over-capacity",
              techName: tech?.userName || "Technician",
            });
            return;
          }
        }

        // If capacity is sufficient, allow the drop (even if segment not found in current workOrders)
        // This handles segments from search results that aren't in the current page

        // Get current items to calculate insert position
        const currentItems = schedule.getScheduledItemsForTechDay(techId, day);
        const insertAt = Math.min(position, currentItems.length);
        // CRITICAL: Pass the date to ensure workDate is set correctly for the selected week
        // Schedule the item at the specified position
        schedule.scheduleFromDrag(
          dragData,
          techId,
          day,
          requested,
          insertAt,
          undefined,
          date,
        );
      } catch (error) {
        console.error(" Failed to parse drag data:", error);
      }
    };

  // Combined loading state for spinner overlay
  const isLoading = loadingWorkOrders || loadingScheduledItems;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: "background.default",
        position: "relative",
      }}
    >
      {/* Loading Spinner Overlay */}
      <Backdrop
        open={isLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "rgba(255, 255, 255, 0.7)",
        }}
      >
        <CircularProgress color="primary" size={48} />
      </Backdrop>

      {/* Today's Items Alert Banner */}
      {!todayAlertDismissed && todayItemsCount > 0 && initialDataLoaded && (
        <Alert
          severity="info"
          sx={{
            borderRadius: 0,
            py: 0.5,
            alignItems: "center",
            "& .MuiAlert-icon": {
              alignItems: "center",
            },
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            },
            "& .MuiAlert-action": {
              pt: 0,
              alignItems: "center",
            },
          }}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleViewTodayClick}
                sx={{
                  fontWeight: 600,
                  textTransform: "capitalize",
                  whiteSpace: "nowrap",
                }}
              >
                View Today
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setTodayAlertDismissed(true)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            You have {todayItemsCount} item{todayItemsCount !== 1 ? "s" : ""}{" "}
            scheduled for today that need attention.
          </Typography>
        </Alert>
      )}

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          bgcolor: "background.default",
        }}
      >
        {/* Left Sidebar - Work Orders */}
        <Box
          sx={{
            maxHeight: "95%",
            width: leftSidebarCollapsed ? 48 : 300,
            borderRight: 2,
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
            boxShadow: "2px 0 8px rgba(0, 0, 0, 0.05)",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <WorkOrdersSidebar
            workOrders={schedule.state.workOrders}
            segmentParts={schedule.state.segmentParts}
            reservedCapacity={schedule.state.reservedCapacity}
            isBrokenMap={isBrokenMap}
            locations={schedule.state.locations}
            onDragStart={dragDrop.handleDragStart}
            onDragEnd={dragDrop.handleDragEnd}
            isSegmentBroken={() => false} // Always allow - was: schedule.isSegmentBroken
            onUpdateSegmentHours={(segmentId, newHours) =>
              schedule.updateSegmentHours(segmentId, newHours)
            }
            onBreakSegment={handleSegmentClick}
            onConcurrencyError={(message) => {
              setConcurrencyMessage(message);
              setShowConcurrencyDialog(true);
            }}
            collapsed={leftSidebarCollapsed}
            onToggleCollapse={() =>
              setLeftSidebarCollapsed(!leftSidebarCollapsed)
            }
            resetSearchTrigger={resetSearchTrigger}
            pageNumber={workOrderPage}
            totalPages={workOrderTotalPages}
            totalCount={workOrderTotalCount}
            onPageChange={(page) => {
              setWorkOrderPage(page);
              fetchWorkOrders(
                selectedLocationIds,
                page,
                workOrderSearchString,
                selectedWoStatusIds,
                selectedWoDocStatusIds,
                segmentSchedulingStatuses,
              );
            }}
            onSearchChange={handleSearchChange}
            loading={loadingWorkOrders}
            initialSearchValue={initialSearchQuery}
            segmentSchedulingStatuses={segmentSchedulingStatuses}
            onSegmentSchedulingStatusesChange={
              handleSegmentSchedulingStatusesChange
            }
          />
        </Box>

        {/* Center - Capacity Board */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "auto", // Enable scrolling - CapacityBoard content may exceed container width
            bgcolor: "background.default",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            minWidth: 0, // Allow flex item to shrink below min-content
            maxHeight: "92.5%", // Ensure it takes full height
            // Horizontal scrollbar styling - make it more visible
            "&::-webkit-scrollbar": {
              height: "14px", // Thicker horizontal scrollbar
              width: "14px", // Thicker vertical scrollbar
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#2a2a2a" : "#e0e0e0",
              borderRadius: "7px",
              border: "2px solid transparent",
              backgroundClip: "padding-box",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#6b6b6b" : "#9e9e9e",
              borderRadius: "7px",
              border: "2px solid transparent",
              backgroundClip: "padding-box",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#888888" : "#757575",
              },
              "&:active": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#999999" : "#616161",
              },
            },
            "&::-webkit-scrollbar-corner": {
              bgcolor: "transparent",
            },
          }}
        >
          {/* 
            CONDITIONAL RENDERING: CapacityBoard only renders when all required data is loaded
             
            This prevents capacity calculations from running with incomplete data:
             
            Scenario 1 - First Page Load:
              - initialDataLoaded starts as false
              - CapacityBoard does not render initially
              - As data syncs from cache/store, initialDataLoaded becomes true
              - Scheduled items are fetched via useEffect
              - CapacityBoard then renders with complete data including scheduled items
             
            Scenario 2 - Page Refresh:
              - Store loads from cache immediately
              - Data syncs to schedule.state via useEffects
              - Once all critical data is synced, initialDataLoaded becomes true
              - Scheduled items are fetched via useEffect (ensures they're loaded)
              - CapacityBoard renders with real data (not seed data) and scheduled items
             
            Why this matters:
              - Prevents capacity calculations with seed data
              - Ensures scheduled items are loaded before capacity calculations
              - Ensures accurate capacity display on first load and refresh
              - Avoids flickering/incorrect values during data loading
             
            Note: Scheduled items are fetched independently and don't block rendering,
            but they're fetched as soon as initialDataLoaded is true, ensuring they're
            available for capacity calculations.
          */}
          {!initialDataLoaded ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 240,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <CapacityBoard
              technicians={schedule.state.technicians}
              days={schedule.DAYS}
              scheduledItems={schedule.state.scheduledItems}
              availableHours={schedule.getAvailableHours}
              getBaseCapacity={schedule.getBaseCapacity}
              getScheduledItemsForTechDay={schedule.getScheduledItemsForTechDay}
              workOrders={schedule.state.workOrders}
              segmentParts={schedule.state.segmentParts}
              locations={schedule.state.locations}
              businessHours={schedule.state.businessHours}
              holidays={schedule.state.holidays}
              employeeSchedules={schedule.state.employeeSchedules}
              employeeScheduleExceptions={
                schedule.state.employeeScheduleExceptions
              }
              darkMode={darkMode}
              onThemeToggle={() => setDarkMode(!darkMode)}
              onItemRemove={async (itemId) => {
                await schedule.removeScheduledItem(itemId);
                refreshWorkOrdersWithCurrentFilters();
              }}
              onItemsRemove={async (itemIds) => {
                await schedule.removeScheduledItems(itemIds);
                refreshWorkOrdersWithCurrentFilters();
              }}
              onItemComplete={async (itemId) => {
                await schedule.completeScheduledItem(itemId);
                fetchScheduledItemsForWeek(selectedLocationIds, weekOffset);
              }}
              onDropZone={handleDropOnCard}
              onDropAtPosition={handleDropAtPosition}
              onReorderItems={(techId, day, fromItemId, toSortKey) =>
                schedule.reorderScheduledItems(
                  techId,
                  day,
                  fromItemId,
                  toSortKey,
                )
              }
              onUpdateItemHours={(itemId, newHours) =>
                schedule.updateScheduledItemHours(itemId, newHours)
              }
              onResetScheduleCache={async () => {
                await schedule.refreshSchedulingCache();
                await refreshVisibleCalendar();
              }}
              onResetFilters={() => {
                // CRITICAL: Set reset flag FIRST to prevent duplicate API calls from useEffect
                // The useEffect watching weekOffset/selectedLocationIdsFromStore will check this flag and skip
                isResettingRef.current = true;

                // Reset all breakdowns FIRST (before fetching) to avoid state update loops
                // Get unique work order IDs from segment parts
                const workOrderIdsWithBreakdowns = new Set(
                  schedule.state.segmentParts.map(
                    (part) => part.parentWorkOrderId,
                  ),
                );
                workOrderIdsWithBreakdowns.forEach((workOrderId) => {
                  schedule.removeBreakdown(workOrderId);
                });
                // Reset search query state and UI
                setWorkOrderSearchString(""); // Clear search string state
                setResetSearchTrigger((prev) => prev + 1); // Reset search UI in sidebar
                setSegmentSchedulingStatuses(DEFAULT_SEGMENT_SCHEDULING_FILTERS);

                // Reset uses "All" locations (empty array for API)
                // CapacityBoard already set selectedLocationIds to ["0"] before calling this
                const locationIdsToUse: number[] = []; // Empty array = All locations

                // Reset page and update location state
                setSelectedLocationIds(locationIdsToUse);
                setWorkOrderPage(1);

                // Keep ref in sync immediately so any concurrent refresh uses defaults
                workOrderQueryRef.current = {
                  locationIds: locationIdsToUse,
                  pageNumber: 1,
                  searchString: "",
                  woStatusIds: ["0"],
                  woDocStatusIds: ["0"],
                  segmentSchedulingStatuses: DEFAULT_SEGMENT_SCHEDULING_FILTERS,
                };

                // Fetch work orders with "All" locations and reset WO status
                fetchWorkOrders(
                  locationIdsToUse,
                  1,
                  "",
                  ["0"],
                  ["0"],
                  DEFAULT_SEGMENT_SCHEDULING_FILTERS,
                );

                // Fetch scheduled items for current week (weekOffset=0) with "All" locations
                fetchScheduledItemsForWeek(locationIdsToUse, 0);
              }}
              onLocationChange={handleLocationChange}
              onViewModeChange={(viewMode, contextDate) => {
                // Switch active view immediately (prevents month data showing in week/day, etc.)
                activeViewModeRef.current = viewMode;
                schedule.setScheduledItems(
                  scheduledItemsByViewRef.current[viewMode] || [],
                );

                // Fetch data for the selected view
                // Convert store's string array to number array, filtering out "0" (All)
                const locationIdsToUse = selectedLocationIdsFromStore.includes(
                  "0",
                )
                  ? []
                  : selectedLocationIdsFromStore
                      .map((id) => parseInt(id, 10))
                      .filter((id) => !isNaN(id));

                if (viewMode === "day") {
                  dayDateRef.current = contextDate;
                  fetchScheduledItemsForDay(locationIdsToUse, contextDate);
                } else if (viewMode === "month") {
                  monthDateRef.current = contextDate;
                  fetchScheduledItemsForMonth(locationIdsToUse, contextDate);
                } else {
                  // Week view: default to current week on tab switch
                  fetchScheduledItemsForWeek(locationIdsToUse, 0);
                }
              }}
              onDayViewDateChange={(date: Date) => {
                // Fetch scheduled items for the selected day in Day view
                const locationIdsToUse = selectedLocationIdsFromStore.includes(
                  "0",
                )
                  ? []
                  : selectedLocationIdsFromStore
                      .map((id) => parseInt(id, 10))
                      .filter((id) => !isNaN(id));
                fetchScheduledItemsForDay(locationIdsToUse, date);
              }}
              onMonthViewDateChange={(date: Date) => {
                // Fetch scheduled items for the selected month in Month view
                const locationIdsToUse = selectedLocationIdsFromStore.includes(
                  "0",
                )
                  ? []
                  : selectedLocationIdsFromStore
                      .map((id) => parseInt(id, 10))
                      .filter((id) => !isNaN(id));
                fetchScheduledItemsForMonth(locationIdsToUse, date);
              }}
              onOpenWorkOrder={handleOpenWorkOrder}
              onOpenSmartWo={(technicianId, date) =>
                setSmartWoDialogState({
                  open: true,
                  technicianId,
                  date,
                })
              }
              triggerDayView={triggerDayView}
            />
          )}
        </Box>

        {/* Right Sidebar - Segment Parts */}
        <Box
          sx={{
            width: rightSidebarCollapsed ? 48 : 250,
            borderLeft: 2,
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
            boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.05)",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
          }}
        >
          <SegmentPartsSidebar
            segmentParts={schedule.state.segmentParts}
            workOrders={schedule.state.workOrders}
            onDragStart={dragDrop.handleDragStart}
            onDragEnd={dragDrop.handleDragEnd}
            onRemoveBreakdown={(workOrderId) =>
              schedule.removeBreakdown(workOrderId)
            }
            collapsed={rightSidebarCollapsed}
            onToggleCollapse={() =>
              setRightSidebarCollapsed(!rightSidebarCollapsed)
            }
          />
        </Box>
      </Box>

      {/* Modals */}
      <BreakSegmentModal
        segment={selectedSegment?.segment || null}
        workOrder={selectedSegment?.workOrder || null}
        isOpen={breakModalOpen}
        onClose={() => {
          setBreakModalOpen(false);
          setSelectedWorkOrderId(null);
          setSelectedSegmentFromDrag(null);
          setCapacityWarning(null);
          setOverrideRequest(null);
        }}
        onConfirm={handleBreakSegment}
        onGoOverCapacity={handleGoOverCapacity}
        capacityWarning={capacityWarning || undefined}
      />

      <Dialog
        open={Boolean(
          overrideRequest &&
          (overrideRequest.reason === "tech-off" ||
            (overrideRequest.reason === "over-capacity" &&
              overrideRequest.dragData?.type !== "segment")),
        )}
        onClose={() => setOverrideRequest(null)}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            pr: 1,
          }}
        >
          {overrideRequest?.reason === "tech-off"
            ? "Technician Is Off"
            : "Over Capacity"}
          <IconButton
            aria-label="close"
            onClick={() => setOverrideRequest(null)}
            sx={{
              color: "primary.contrastText",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{}}>
            {overrideRequest?.reason === "tech-off"
              ? `${overrideRequest?.techName} is not scheduled to work on ${capitalizeFirstLetter(
                  overrideRequest?.day || "",
                )}. Do you want to schedule anyway?`
              : `This will exceed the available capacity for ${overrideRequest?.techName} on ${capitalizeFirstLetter(
                  overrideRequest?.day || "",
                )}. Do you want to schedule anyway?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOverrideRequest(null)}
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={overrideRequest?.reason === "tech-off" ? "error" : "warning"}
            onClick={handleGoOverCapacity}
            sx={{ textTransform: "capitalize" }}
          >
            {overrideRequest?.reason === "tech-off"
              ? "Schedule Anyway"
              : "Go Over Capacity"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for location closed/holiday warnings */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            minWidth: 0,
            padding: 0,
          },
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="warning"
          variant="filled"
          sx={{
            minWidth: 320,
            maxWidth: 450,
            fontSize: "0.875rem",
            fontWeight: 500,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
            borderRadius: 2,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
              alignItems: "center",
            },
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              py: 0.5,
              color: "white",
            },
            "& .MuiAlert-action": {
              alignItems: "center",
              paddingTop: 0,
              paddingBottom: 0,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Work Order Dialog for opening work orders from scheduled items */}
      {showWorkOrderDialog && dialogWorkOrderId && (
        <WorkOrderDialogContainer
          showHideWoPopup={showHideWorkOrderPopup}
          InvoiceDocIdentity={dialogWorkOrderId}
          open={true}
        />
      )}

      {/* Restore Cached Segment Edit Confirmation Dialog */}
      <Dialog
        open={showRestoreSegmentDialog}
        onClose={handleRestoreSegmentDecline}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Restore Previous Changes?</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            We found your unsaved segment hours edit from your last session.
            Would you like to restore it?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            If you choose "No", the changes will be discarded.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleRestoreSegmentDecline}
            variant="outlined"
            color="inherit"
          >
            No
          </Button>
          <Button
            onClick={handleRestoreSegmentConfirm}
            variant="contained"
            color="primary"
          >
            Yes, Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Concurrency Conflict Dialog */}
      <ConcurrencyDialog
        open={showConcurrencyDialog}
        onDismiss={() => {
          setShowConcurrencyDialog(false);
        }}
        onRefresh={() => {
          window.location.reload();
        }}
        message={concurrencyMessage}
      />

      <SmartWoCreationDialog
        open={smartWoDialogState.open}
        onClose={() => setSmartWoDialogState({ open: false })}
        preSelectedTechnicianId={smartWoDialogState.technicianId}
        preSelectedDate={smartWoDialogState.date}
        onSuccess={() => {
          window.dispatchEvent(new CustomEvent("refreshTechnicianCalendar"));
        }}
      />
    </Box>
  );
}
